import React, { useState, useEffect } from 'react';
import { useSubscription } from '../context/SubscriptionContext';
import { STRIPE_PLANS, formatAmount } from '../config/stripe';
import { createCheckoutSession, verifyCheckoutSession } from '../services/stripeService';

const StripePayment = ({ 
  planType = 'premium', 
  onSuccess, 
  onError, 
  onCancel,
  isVisible = true 
}) => {
  const { updateSubscriptionFromStripe } = useSubscription();
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState(null);

  // Get current user ID from sessionStorage
  const getUserId = () => {
    return sessionStorage.getItem('userId') || sessionStorage.getItem('firebaseUid');
  };

  // Get user email from sessionStorage
  const getUserEmail = () => {
    return sessionStorage.getItem('userEmail');
  };

  const handleCheckout = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const userId = getUserId();
      if (!userId) {
        throw new Error('User not authenticated. Please log in.');
      }

      const userEmail = getUserEmail();
      const plan = STRIPE_PLANS[planType];

      if (!plan || !plan.priceId || plan.priceId.includes('placeholder')) {
        throw new Error('Subscription plan not configured. Please contact support.');
      }

      // Create checkout session
      const session = await createCheckoutSession(planType, userId, userEmail);

      if (session && session.url) {
        // Redirect to Stripe Checkout
        window.location.href = session.url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Stripe Checkout Error:', error);
      setError(error.message || 'Failed to start checkout. Please try again.');
      setIsLoading(false);
      if (onError) {
        onError(error);
      }
    }
  };

  // Check for successful payment return and verify with Stripe
  useEffect(() => {
    if (!isVisible) return;

    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const sessionId = urlParams.get('session_id');
    const canceled = urlParams.get('canceled');

    if (success === 'true' && sessionId) {
      // Verify payment with Stripe API
      const verifyPayment = async () => {
        try {
          setIsVerifying(true);
          setError(null);

          // Verify checkout session with Stripe
          const verifiedSession = await verifyCheckoutSession(sessionId);

          if (verifiedSession.session.payment_status === 'paid' && verifiedSession.subscription) {
            // Payment verified - update subscription from Stripe data
            const stripeData = {
              subscription: verifiedSession.subscription,
              customer: { id: verifiedSession.session.customer },
            };
            
            updateSubscriptionFromStripe(stripeData);

            // Migrate from localStorage - clear it since we now have Stripe subscription
            localStorage.removeItem('userSubscription');

            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);

            if (onSuccess) {
              onSuccess({ sessionId, subscription: verifiedSession.subscription });
            }
          } else {
            throw new Error('Payment verification failed');
          }
        } catch (error) {
          console.error('Payment verification error:', error);
          setError('Payment verification failed. Please contact support if payment was processed.');
          // Clean up URL even on error
          window.history.replaceState({}, document.title, window.location.pathname);
          if (onError) {
            onError(error);
          }
        } finally {
          setIsVerifying(false);
        }
      };

      verifyPayment();
    } else if (canceled === 'true') {
      // Payment canceled
      window.history.replaceState({}, document.title, window.location.pathname);
      if (onCancel) {
        onCancel();
      }
    }
  }, [isVisible, planType, updateSubscriptionFromStripe, onSuccess, onCancel, onError]);

  if (!isVisible) {
    return null;
  }

  const plan = STRIPE_PLANS[planType] || STRIPE_PLANS.premium;

  return (
    <div className="adyen-payment-container">
      <div className="adyen-payment-header">
        <h3>Secure Payment</h3>
        <p>
          Complete your <strong>{plan.name}</strong> plan subscription for{' '}
          <strong>{formatAmount(plan.amount)}</strong> per month
        </p>
      </div>

      {error && (
        <div className="adyen-error-message">
          <div className="error-icon">⚠️</div>
          <div className="error-text">{error}</div>
        </div>
      )}

      {isVerifying && (
        <div className="adyen-loading">
          <div className="loading-spinner"></div>
          <p>Verifying payment...</p>
        </div>
      )}

      <div className="adyen-payment-info">
        <h4>Recurring Billing Information</h4>
        <ul>
          <li>Your subscription will automatically renew monthly</li>
          <li>You can cancel anytime from your account settings</li>
          <li>You'll receive email confirmations for all payments</li>
          <li>Your payment method will be securely stored for future billing</li>
        </ul>
      </div>

      <div className="adyen-payment-footer">
        <div className="security-info">
          <div className="security-icon">🔒</div>
          <div className="security-text">
            <p><strong>Secure Payment</strong></p>
            <p>Your payment information is encrypted and secure</p>
          </div>
        </div>
        
        <div className="payment-actions">
          <button 
            type="button" 
            className="cancel-payment-btn"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className="primary-btn"
            onClick={handleCheckout}
            disabled={isLoading || plan.priceId.includes('placeholder')}
          >
            {isLoading ? 'Processing...' : `Subscribe to ${plan.name} Plan`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StripePayment;

